import { Link } from "react-router-dom";
import { ArrowRight, FlaskConical, Rocket, Gauge, RefreshCcw, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import CTAStrip from "@/components/site/CTAStrip";
import RelatedLinks from "@/components/site/RelatedLinks";
import { useSEO } from "@/lib/seo";
import { linkifyGoAndTrack } from "@/lib/goAndTrack";

const IMG = "/images/ll100-peel.jpg";

const STAGES = [
  { n: "01", k: "Scope", v: "Define the lane, shipment profile, KPIs, and decision threshold for the pilot." },
  { n: "02", k: "Deploy", v: "Apply disposable smart labels on shipments in real operating conditions — no infrastructure standup." },
  { n: "03", k: "Observe", v: "Live data flows into GoAndTrack. Exceptions, dwell, and route deviations are captured cleanly." },
  { n: "04", k: "Decide", v: "Structured review against pilot KPIs. Move to programme rollout, expand lanes, or stop with no sunk hardware cost." },
];

export default function PilotProjects() {
  useSEO({
    title: "Smart Labels for Pilot Projects | Small Tracking Devices for Packages",
    description: "Stand up structured logistics pilots in days using disposable smart labels — small tracking devices for packages with no CapEx and no hardware recovery.",
    canonical: "/smart-labels-for-pilot-projects",
  });

  return (
    <div data-testid="page-pilots">
      <PageHero
        eyebrow="Use case"
        title="Smart Labels for Pilot Projects"
        subtitle="Stand up a structured shipment-visibility pilot in days, not quarters. Disposable smart labels remove the hardware-ownership friction that stalls most pilots."
      >
        <Link to="/request-a-trial" className="btn-primary" data-testid="page-cta-trial">
          Speak to an expert <ArrowRight className="h-4 w-4" />
        </Link>
      </PageHero>

      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow mb-4">Why pilot teams choose disposable</p>
            <h2 className="h2">Built for fast, structured experimentation.</h2>
            <p className="lead mt-5">
              Innovation, supply chain, and operations leads use disposable
              smart labels to get clean shipment data into a pilot — without
              triggering a hardware procurement cycle.
            </p>
            <ul className="mt-8 space-y-3.5">
              {[
                "No CapEx commitment to gather pilot evidence.",
                "No hardware-recovery loop blocking quick lane changes.",
                "Per-shipment cost model — easy to model and scale.",
                "Data delivered through GoAndTrack from day one.",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px] text-ink">
                  <CheckCircle2 className="h-4 w-4 mt-1 text-brand shrink-0" />
                  {linkifyGoAndTrack(b)}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="border border-line bg-white">
              <div className="aspect-[5/4] overflow-hidden">
                <img src={IMG} alt="Peeling a disposable smart label — ready to deploy on a pilot shipment lane" className="h-full w-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-surface-subtle border-y border-line">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">Pilot framework</p>
            <h2 className="h2">Four stages from scope to decision.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-line border border-line">
            {STAGES.map((s) => (
              <div key={s.n} className="bg-white p-7">
                <p className="font-mono text-xs text-brand">{s.n}</p>
                <h3 className="font-heading text-lg font-semibold mt-3">{s.k}</h3>
                <p className="mt-3 text-sm text-ink-muted">{linkifyGoAndTrack(s.v)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-x grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
          {[
            { icon: FlaskConical, k: "New lane validation", v: "Test whether a new route, carrier, or partner meets your visibility and performance threshold." },
            { icon: Rocket, k: "Product launch programmes", v: "Time-bound shipment-visibility coverage for launches, recalls, and seasonal campaigns." },
            { icon: Gauge, k: "Performance benchmarking", v: "Build a baseline of dwell, deviation, and exception rates before signing carrier or 3PL agreements." },
            { icon: RefreshCcw, k: "Returns flow trials", v: "Validate visibility on returns lanes where customer-side hardware recovery is impractical." },
          ].map((b) => (
            <div key={b.k} className="bg-white p-7 lg:p-9">
              <b.icon className="h-5 w-5 text-brand" />
              <h3 className="h3 mt-4">{b.k}</h3>
              <p className="body-text mt-2 text-sm">{b.v}</p>
            </div>
          ))}
        </div>
      </section>

      <RelatedLinks
        title="Related reading for pilot leads"
        links={[
          { to: "/disposable-smart-labels", label: "Disposable Smart Labels — category overview" },
          { to: "/smart-labels-for-logistics", label: "Smart Labels for Logistics — lane-level visibility" },
          { to: "/disposable-vs-reusable-trackers", label: "Disposable vs Reusable Trackers — full comparison" },
          { to: "/faqs", label: "FAQs — pilot process, integration, and activation" },
        ]}
      />

      <CTAStrip
        title="Run your pilot in real lanes — not slide decks."
        body="We'll scope a structured pilot, deploy labels, and review results against your KPIs."
      />
    </div>
  );
}
